import { coalesce } from "$root/utils";
import type { ColDef, ColDefField } from "ag-grid-community";
import { dateColDef } from "../custom-column/date/base-cell";
import { markdownColDef } from "../custom-column/markdown/base-cell";

export type ColumnMandatoryParams = {
	headerName: string;
	colId: string;
	editable?: boolean;
};

export type ColumnOverrideParams<T, U = any> = Omit<ColDef<T, U>, "field" | "cellDataType">;

export const commonColumnsDef = {
	string<T>(
		field: ColDefField<T, string | undefined>,
		opt?: ColumnOverrideParams<T, string | undefined>,
	): ColDef<T, string | undefined> {
		return {
			field,
			tooltipField: field,
			headerName: opt?.headerName ?? field,
			colId: opt?.colId ?? field,
			cellDataType: "text",
			...coalesce(opt, {}),
		};
	},
	markdown<T>(
		field: ColDefField<T, string | undefined>,
		opt?: ColumnOverrideParams<T, string | undefined>,
	): ColDef<T, string | undefined> {
		const markdownDef = markdownColDef();
		return {
			field,
			tooltipField: field,
			headerName: opt?.headerName ?? field,
			colId: opt?.colId ?? field,
			cellEditor: "agLargeTextCellEditor",
			cellEditorPopup: true,
			cellEditorParams: {
				rows: 10,
				cols: 50,
				maxLength: 100000,
			},
			cellRenderer: markdownDef.cellRenderer,
			tooltipComponent: markdownDef.tooltipRender,
			...coalesce(opt, {}),
		};
	},
	percentage<T>(
		field: ColDefField<T, number | undefined>,
		opt?: ColumnOverrideParams<T, number | undefined>,
	): ColDef<T, number | undefined> {
		return {
			field,
			headerName: opt?.headerName ?? field,
			colId: opt?.colId ?? field,
			cellDataType: "percentage",
			editable: opt?.editable,
			cellEditor: "agNumberCellEditor",
			cellEditorParams: {
				min: 0,
				max: 100,
				step: 0.01,
				precision: 4,
			},
			...coalesce(opt, {}),
			valueParser({ newValue, oldValue }) {
				if (newValue == null || newValue === "") {
					return undefined;
				}

				if (typeof newValue === "number") {
					return newValue;
				}

				let strValue = String(newValue).trim();
				const hasPercentSign = strValue.includes("%");
				strValue = strValue.replace("%", "").trim();
				const numValue = parseFloat(strValue);

				if (isNaN(numValue)) {
					return oldValue;
				}

				if (hasPercentSign) {
					return numValue;
				}

				return numValue;
			},
			cellClassRules: {
				"ag-cell--monospace-font": () => {
					return true;
				}
			}
		};
	},
	floating<T>(
		field: ColDefField<T, number | undefined>,
		opt?: ColumnOverrideParams<T, number | undefined>,
	): ColDef<T, number | undefined> {
		return {
			field,
			headerName: opt?.headerName ?? field,
			colId: opt?.colId ?? field,
			cellDataType: "floating",
			...coalesce(opt, {}),
		};
	},
	integer<T>(
		field: ColDefField<T, number | undefined>,
		opt?: ColumnOverrideParams<T, number | undefined>,
	): ColDef<T, number | undefined> {
		return {
			field,
			headerName: opt?.headerName ?? field,
			colId: opt?.colId ?? field,
			cellDataType: "integer",
			...coalesce(opt, {}),
		};
	},
	date<T>(
		field: ColDefField<T, string | undefined>,
		opt?: ColumnOverrideParams<T, string | undefined>,
	): ColDef<T, string | undefined> {
		const dateDef = dateColDef<T>();
		return {
			field,
			headerName: opt?.headerName ?? field,
			colId: opt?.colId ?? field,
			cellDataType: "mdotmDate",
			cellRenderer: dateDef.cellRenderer,
			cellEditor: dateDef.cellEditor,
			cellEditorPopup: dateDef.cellEditorPopup,
			cellEditorPopupPosition: dateDef.cellEditorPopupPosition,
			suppressKeyboardEvent: dateDef.suppressKeyboardEvent,
			...coalesce(opt, {}),
		};
	},
};
